import type { Point, Vehicle, Station, SimState } from '../domain/model';
export interface RouteEstimate {
  distanceKm: number;
  durationSeconds: number;
  geometry: Point[];
  segmentSeconds?: number[];
  estimated: boolean;
}
export interface RoutingProvider {
  readonly id: string;
  estimate(from: Point, to: Point): RouteEstimate;
}
export function distanceKm(a: Point, b: Point) {
  const r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * r,
    dLon = (b[1] - a[1]) * r;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(Math.max(0, 1 - h)));
}
// Ground routes are resolved asynchronously by the server, never replaced by a straight line.
export const pendingRouting: RoutingProvider = {
  id: 'pending-road',
  estimate: () => {
    throw Error('Straßenroute muss zuerst berechnet werden.');
  },
};
export const isAircraft = (kind: Vehicle['kind']) => kind === 'RTH' || kind === 'ITH';
export function estimateVehicleRoute(
  kind: Vehicle['kind'],
  from: Point,
  to: Point,
  provider = pendingRouting,
): RouteEstimate {
  if (isAircraft(kind) && provider.id === pendingRouting.id) {
    const distance = distanceKm(from, to);
    return {
      distanceKm: distance,
      durationSeconds: Math.max(1, (distance / 180) * 3600),
      geometry: [from, to],
      estimated: true,
    };
  }
  return provider.estimate(from, to);
}
// Simulation assumptions, not measured response times. Already mobile units leave immediately.
export function turnoutSeconds(v: Vehicle, station?: Station, returning = false) {
  if (returning || v.status !== 2) return 0;
  if (v.policeSupport) return v.policeSupport.preparationSeconds;
  if (isAircraft(v.kind)) return 120;
  if (v.kind === 'HvO') return 120;
  if (/Freiwill|Ortsverein|Bereitschaft/i.test(`${station?.name} ${station?.stationType}`))
    return 180;
  if (station?.service === 'Polizei') return 45;
  return 60;
}
export function vehicleTravelFactor(kind: Vehicle['kind']) {
  return ['HLF', 'LF', 'DLK', 'TLF', 'RW', 'GW', 'GKW', 'WaWe', 'GW-L', 'WLF'].includes(kind)
    ? 1.1
    : 1;
}
export type Journey = NonNullable<Vehicle['journey']>;
export function createJourney(
  v: Vehicle,
  to: Point,
  now: number,
  returning: boolean,
  station?: Station,
  provider = pendingRouting,
): Journey {
  const journey: Journey = {
    from: [...v.location],
    to: [...to],
    start: now,
    end: now + 1,
    returning,
    requestId: globalThis.crypto.randomUUID(),
    routeStatus: 'pending',
    turnoutSeconds: provider.id === pendingRouting.id ? turnoutSeconds(v, station, returning) : 0,
  };
  if (provider.id !== pendingRouting.id || isAircraft(v.kind)) {
    const route = estimateVehicleRoute(v.kind, v.location, to, provider);
    return resolveJourney(journey, route, now, isAircraft(v.kind) ? 'air' : 'test');
  }
  return journey;
}
export function resolveJourney(
  journey: Journey,
  route: RouteEstimate,
  now: number,
  provider: Journey['provider'] = 'osrm',
): Journey {
  if (
    route.geometry.length < 2 ||
    !Number.isFinite(route.durationSeconds) ||
    route.durationSeconds <= 0
  )
    throw Error('Ungültige Straßenroute.');
  const duration = Math.max(1, route.durationSeconds);
  const weights =
    route.segmentSeconds?.length === route.geometry.length - 1
      ? route.segmentSeconds
      : route.geometry.slice(1).map((p, i) => distanceKm(route.geometry[i], p));
  const sum = weights.reduce((a, b) => a + b, 0);
  const segmentSeconds = weights.map((value) =>
    sum > 0 ? (value / sum) * duration : duration / weights.length,
  );
  const departureAt = Math.max(now, journey.start + (journey.turnoutSeconds ?? 0) * 1000);
  return {
    ...journey,
    routeStatus: 'ready',
    routeError: undefined,
    provider,
    from: [...route.geometry[0]],
    to: [...route.geometry.at(-1)!],
    geometry: route.geometry,
    segmentSeconds,
    distanceKm: route.distanceKm,
    departureAt,
    end: departureAt + duration * 1000,
  };
}
export function journeyPosition(journey: Journey, now: number) {
  const points = journey.geometry;
  if (journey.routeStatus !== 'ready' || !points || points.length < 2)
    return { position: journey.from, segment: 0, arrived: false };
  const departure = journey.departureAt ?? journey.start;
  const duration = Math.max(1, (journey.end - departure) / 1000);
  let remaining = Math.max(0, (now - departure) / 1000);
  if (now >= journey.end)
    return { position: points.at(-1)!, segment: points.length - 2, arrived: true };
  for (let i = 0; i < points.length - 1; i++) {
    const seconds = journey.segmentSeconds?.[i] ?? duration / (points.length - 1);
    if (remaining <= seconds && seconds > 0) {
      const fraction = remaining / seconds;
      return {
        position: [
          points[i][0] + (points[i + 1][0] - points[i][0]) * fraction,
          points[i][1] + (points[i + 1][1] - points[i][1]) * fraction,
        ] as Point,
        segment: i,
        arrived: false,
      };
    }
    remaining -= seconds;
  }
  return { position: points.at(-1)!, segment: points.length - 2, arrived: false };
}
export function migrateLegacyJourneys(state: SimState) {
  const next = structuredClone(state);
  for (const v of next.vehicles) {
    const old = v.journey;
    if (!old || (old.requestId && old.routeStatus && (old.routeStatus !== 'ready' || old.geometry)))
      continue;
    v.journey = createJourney(v, old.to, next.now, old.returning);
    v.journey.turnoutSeconds = 0;
  }
  return next;
}
